import React, { useMemo, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../services/api';
import WorkOrdersCalendarPage from './WorkOrdersCalendarPage';
import { WorkOrderDto, PriorityDto, User, StatusDto } from '../types';
import { Checkbox } from '../components/ui/checkbox';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import SearchableSelect from '../components/ui/SearchableSelect';
import { Eye, Edit, Trash2, Users, ChevronsRight, Search, ChevronUp, ChevronDown, Settings, Printer, Mail, LayoutGrid, Calendar as CalendarIcon, ClipboardList, Plus, Wrench, FileInput } from 'lucide-react';
import { useDebounce } from '../hooks/useDebounce';
import BulkEditModal from '../components/workorders/BulkEditModal';
import EmailImportModal from '../components/workorders/EmailImportModal';
import toast from 'react-hot-toast';
import Modal from '../components/ui/Modal';
import ConfirmDialog from '../components/modals/ConfirmDialog';

const avatarColor = (name?: string) => {
  if (!name) return '#6b7280';
  let hash = 0; for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  const hue = Math.abs(hash) % 360; return `hsl(${hue}, 60%, 65%)`;
};

const WorkOrdersPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // filters/search/sort/pagination parity (basic placeholders; reuse existing endpoints)
  const [search, setSearch] = useState('');
  const [assignedToFilter, setAssignedToFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('');
  const [sortField, setSortField] = useState<string>('createdDate');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [viewMode, setViewMode] = useState<'grid'|'calendar'>(() => (localStorage.getItem('wo-v2-view') as 'grid'|'calendar') || 'grid');
  const pageSize = 25;
  const [activeTab, setActiveTab] = useState<string>('all');
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  // Bulk edit state
  const [isBulkEditModalOpen, setIsBulkEditModalOpen] = useState(false);
  const [isEmailImportModalOpen, setIsEmailImportModalOpen] = useState(false);

  // Email modal state
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [selectedWorkOrderForEmail, setSelectedWorkOrderForEmail] = useState<WorkOrderDto | null>(null);

  // Delete confirmation state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [workOrderToDelete, setWorkOrderToDelete] = useState<WorkOrderDto | null>(null);

  const debouncedSearch = useDebounce(search, 300);

  const { data: prioritiesData } = useQuery({
    queryKey: ['priorities'],
    queryFn: () => apiService.getPriorities(),
    staleTime: 60_000
  });

  const { data: usersData } = useQuery({
    queryKey: ['company-users'],
    queryFn: () => apiService.getCompanyUsers(),
    staleTime: 60_000
  });

  const { data: statusesData } = useQuery({
    queryKey: ['statuses'],
    queryFn: () => apiService.getStatuses(),
    staleTime: 60_000
  });

  const { data: workOrdersResp, isLoading } = useQuery({
    queryKey: ['workorders-v2', { page, pageSize, search: debouncedSearch, assignedToFilter, priorityFilter, sortField, sortDirection, activeTab }],
    queryFn: async () => {
      const res = await apiService.getWorkOrders(page, pageSize, {
        search: debouncedSearch || undefined,
        assignedTo: assignedToFilter && assignedToFilter !== 'all' ? assignedToFilter : undefined,
        priorityId: priorityFilter && priorityFilter !== 'all' ? parseInt(priorityFilter) : undefined,
        sortBy: sortField,
        sortDirection,
      });
      return res;
    },
    keepPreviousData: true
  });

  const priorities: PriorityDto[] = (prioritiesData as any)?.data || prioritiesData || [];
  // Assign-to must load all active company users (not a single page of /users)
  const users: User[] = ((usersData as any)?.data || []) as User[];
  const assignedToOptions = useMemo(
    () => [
      { value: 'all', label: 'All Users' },
      ...users
        .filter((user) => !!user.id)
        .map((user) => ({
          value: user.id.toString(),
          label: `${user.firstName} ${user.lastName}`.trim() || user.email || 'Unknown user',
        })),
    ],
    [users]
  );
  const statuses: StatusDto[] = (statusesData as any)?.data || statusesData || [];
  const isPaged = !!(workOrdersResp?.data && (workOrdersResp.data as any).data);
  const items: WorkOrderDto[] = isPaged ? (workOrdersResp!.data as any).data : (workOrdersResp?.data ?? []);
  const total = isPaged ? (workOrdersResp!.data as any).totalCount : Array.isArray(items) ? items.length : 0;

  // Apply TabGroup filtering
  const filtered = useMemo(() => {
    if (!items?.length) return [] as WorkOrderDto[];
    return items.filter(w => {
      if (activeTab === 'all') return true;
      if (activeTab === 'unassigned') return !w.assignedUserName;
      if (activeTab.startsWith('group:')) {
        const group = activeTab.substring('group:'.length);
        const status = statuses.find(s => s.id === w.statusId);
        return ((status as any)?.tabGroup || 'Active Work') === group;
      }
      return true;
    });
  }, [items, activeTab, statuses]);

  // Status tab counts (computed from current items without applying activeTab)
  const groupedTabCounts = useMemo(() => {
    const counts: Record<string, number> = { all: 0, unassigned: 0 };
    if (!items?.length) return counts;
    counts.all = items.length;
    counts.unassigned = items.filter(w => !w.assignedUserName).length;
    // Map statusId -> tabGroup
    const idToGroup = new Map<number, string>(statuses.map(s => [s.id, (s as any).tabGroup || 'Active Work'] as [number, string]));
    for (const w of items) {
      const group = idToGroup.get(w.statusId || 0) || 'Active Work';
      counts[`group:${group}`] = (counts[`group:${group}`] || 0) + 1;
    }
    return counts;
  }, [items, statuses]);

  const toggleSelect = (id: number) => {
    const next = new Set(selectedIds); next.has(id) ? next.delete(id) : next.add(id); setSelectedIds(next);
  };

  const daysRemaining = (due?: string | null) => {
    if (!due) return null; const d = new Date(due).setHours(0,0,0,0); const today = new Date().setHours(0,0,0,0);
    const diff = Math.round((d - today) / 86400000);
    return diff;
  };

  const daysColorClass = (d: number | null) => {
    if (d == null) return '';
    if (d < 0) return 'text-red-600 dark:text-red-400 font-semibold';
    if (d <= 2) return 'text-red-600 dark:text-red-400 font-semibold';
    if (d <= 6) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-green-700 dark:text-green-300';
  };

  const priorityColor = (id?: number) => {
    if (!id) return '#9ca3af'; const p = priorities.find(x => x.id === id); return p?.colorCode || '#9ca3af';
  };

  const gotoWO = (id: number) => navigate(`/workorders/${id}/contact`);

  const deleteWorkOrderMutation = useMutation({
    mutationFn: (id: number) => apiService.deleteWorkOrder(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workorders-v2'] });
      setIsDeleteModalOpen(false);
      setWorkOrderToDelete(null);
      toast.success('Work order deleted successfully');
    },
    onError: (error: any) => {
      console.error('Error deleting work order:', error);
      toast.error('Failed to delete work order');
    }
  });

  const handleDelete = useCallback((id: number) => {
    const workOrder = workOrdersResp?.data?.find(wo => wo.id === id);
    if (workOrder) {
      setWorkOrderToDelete(workOrder);
      setIsDeleteModalOpen(true);
    }
  }, [workOrdersResp]);

  const confirmDelete = useCallback(() => {
    if (workOrderToDelete) {
      deleteWorkOrderMutation.mutate(workOrderToDelete.id);
    }
  }, [workOrderToDelete, deleteWorkOrderMutation]);

  const handleSort = useCallback((field: string) => {
    let nextDirection: 'asc' | 'desc' = 'asc';
    if (sortField === field) {
      nextDirection = sortDirection === 'asc' ? 'desc' : 'asc';
    }
    setSortField(field);
    setSortDirection(nextDirection);
  }, [sortField, sortDirection]);

  // Bulk edit handlers
  const handleBulkEditSuccess = useCallback(() => {
    setSelectedIds(new Set());
    setIsBulkEditModalOpen(false);
    queryClient.invalidateQueries({ queryKey: ['workOrdersV2'] });
    toast.success('Work orders updated successfully');
  }, [queryClient]);

  const handleBulkEdit = useCallback(() => {
    if (selectedIds.size === 0) {
      toast.error('Please select work orders to edit');
      return;
    }
    setIsBulkEditModalOpen(true);
  }, [selectedIds.size]);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const getSortIcon = (field: string) => {
    const isActive = sortField === field;
    return (
      <span className="inline-flex items-center w-4 h-4 ml-1">
        {isActive && (sortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />)}
      </span>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-gray-900 dark:text-gray-100" />
            <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Work Orders</h1>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Manage and track all work orders
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="inline-flex rounded-md border border-gray-200 dark:border-gray-700 overflow-hidden">
            <button
              className={`px-3 py-1.5 text-sm inline-flex items-center gap-2 ${viewMode==='grid'?'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100':'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}
              onClick={()=>{ setViewMode('grid'); localStorage.setItem('wo-v2-view','grid'); }}
              title="Grid View"
            >
              <LayoutGrid className="h-4 w-4"/> Grid
            </button>
            <button
              className={`px-3 py-1.5 text-sm inline-flex items-center gap-2 border-l border-gray-200 dark:border-gray-700 ${viewMode==='calendar'?'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100':'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}
              onClick={()=>{ setViewMode('calendar'); localStorage.setItem('wo-v2-view','calendar'); }}
              title="Calendar View"
            >
              <CalendarIcon className="h-4 w-4"/> Calendar
            </button>
          </div>
          <button
            onClick={() => setIsEmailImportModalOpen(true)}
            className="btn-secondary flex items-center space-x-2"
            title="Import Work Order from Email"
          >
            <FileInput className="h-4 w-4" />
            <span>Import from Email</span>
          </button>
          <button
            onClick={() => navigate('/workorders/create')}
            className="btn-primary flex items-center space-x-2"
            title="Create Work Order"
          >
            <Plus className="h-4 w-4" />
            <span>Create Work Order</span>
          </button>
          {/* Classic View link removed after promoting V2 */}
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input
            placeholder="Search by WO#, client, city, vendor..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <SearchableSelect
          className="w-56"
          options={assignedToOptions}
          value={assignedToFilter || 'all'}
          onChange={(value) => setAssignedToFilter(String(value || 'all'))}
          placeholder="Assigned to"
          searchPlaceholder="Search users..."
          allowClear={false}
          aria-label="Filter by assigned user"
        />
        <Select value={priorityFilter} onValueChange={setPriorityFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Priority" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Priorities</SelectItem>
            {priorities.map(priority => (
              <SelectItem key={priority.id} value={priority.id.toString()}>
                {priority.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {/* TabGroup tabs with counts */}
      <div className="flex items-center gap-2">
        {/* All */}
        <button
          onClick={()=>setActiveTab('all')}
          className={`px-3 py-1.5 rounded-md text-sm border ${activeTab==='all'?'bg-gray-100 dark:bg-gray-800 border-gray-300 dark:border-gray-600':'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-700'} ${(groupedTabCounts['all']||0)===0?'opacity-60':''} text-gray-800 dark:text-gray-200`}
          aria-pressed={activeTab==='all'}
        >All ({groupedTabCounts['all']||0})</button>

        {/* Unassigned */}
        <button
          onClick={()=>setActiveTab('unassigned')}
          className={`px-3 py-1.5 rounded-md text-sm border ${activeTab==='unassigned'?'bg-gray-100 dark:bg-gray-800 border-gray-300 dark:border-gray-600':'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-700'} ${(groupedTabCounts['unassigned']||0)===0?'opacity-60':''} text-gray-800 dark:text-gray-200`}
          aria-pressed={activeTab==='unassigned'}
        >Unassigned ({groupedTabCounts['unassigned']||0})</button>

        {/* Grouped tabs based on Status.TabGroup with custom order */}
        {['New Work','Active Work','On Hold','Pending','Complete','Closed'].map(group => {
          const key = `group:${group}`;
          const isActive = activeTab === key;
          const count = groupedTabCounts[key] || 0;
          return (
            <button key={group}
              onClick={()=>setActiveTab(key)}
              className={`px-3 py-1.5 rounded-md text-sm border ${isActive?'bg-gray-100 dark:bg-gray-800 border-gray-300 dark:border-gray-600':'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-700'} ${count===0?'opacity-60':''} text-gray-800 dark:text-gray-200`}
              aria-pressed={isActive}
            >{group} ({count})</button>
          );
        })}
      </div>

      {/* Bulk Actions */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-2 p-3 bg-accent-color-50 dark:bg-accent-color-900/20 border border-accent-color-200 dark:border-accent-color-800 rounded-md">
          <span className="text-sm text-accent-color-800 dark:text-accent-color-200">
            {selectedIds.size} work order{selectedIds.size !== 1 ? 's' : ''} selected
          </span>
          <Button
            onClick={handleBulkEdit}
            size="sm"
            className="btn-primary"
          >
            <Settings className="h-4 w-4 mr-1" />
            Bulk Edit
          </Button>
          <Button
            onClick={clearSelection}
            variant="outline"
            size="sm"
            className="btn-secondary"
          >
            Clear Selection
          </Button>
        </div>
      )}

      {/* Grid or Calendar */}
      {viewMode === 'grid' ? (
      <div className="bg-white dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th className="w-10 px-2 py-1 text-left text-xs font-semibold text-gray-600 dark:text-gray-300"> 
                  <Checkbox checked={selectedIds.size>0 && filtered.every(w=>selectedIds.has(w.id))}
                    onCheckedChange={(v)=>{
                      if (v) setSelectedIds(new Set(filtered.map(w=>w.id))); else setSelectedIds(new Set());
                    }}
                  />
                </th>
                <th className="w-24 px-2 py-1 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('workOrderNumber')}>
                  <div className="flex items-center">
                    WO#
                    {getSortIcon('workOrderNumber')}
                  </div>
                </th>
                <th className="w-52 px-2 py-1 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('clientName')}>
                  <div className="flex items-center">
                    Client
                    {getSortIcon('clientName')}
                  </div>
                </th>
                <th className="w-40 px-2 py-1 text-left text-xs font-semibold text-gray-600 dark:text-gray-300">
                  Equipment
                </th>
                <th className="w-44 px-2 py-1 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('vendorName')}>
                  <div className="flex items-center">
                    Vendor
                    {getSortIcon('vendorName')}
                  </div>
                </th>
                <th className="w-20 px-2 py-1 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('priorityId')}>
                  <div className="flex items-center justify-center">
                    Priority
                    {getSortIcon('priorityId')}
                  </div>
                </th>
                <th className="w-28 px-2 py-1 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('dueDate')}>
                  <div className="flex items-center">
                    Due Date
                    {getSortIcon('dueDate')}
                  </div>
                </th>
                <th className="w-28 px-2 py-1 text-left text-xs font-semibold text-gray-600 dark:text-gray-300">
                  Days Remaining
                </th>
                <th className="w-28 px-2 py-1 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('assignedUserName')}>
                  <div className="flex items-center">
                    Assigned To
                    {getSortIcon('assignedUserName')}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {isLoading ? (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-500">Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-500">No work orders</td></tr>
              ) : filtered.map(w => {
                const d = daysRemaining(w.dueDate);
                const overdue = d != null && d < 0;
                return (
                  <tr key={w.id}
                    className="group hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors"
                    onClick={() => gotoWO(w.id)}
                  >
                    {/* Checkbox */}
                    <td className="px-2 py-1" onClick={(e)=>e.stopPropagation()}>
                      <Checkbox checked={selectedIds.has(w.id)} onCheckedChange={()=>toggleSelect(w.id)} />
                    </td>
                    {/* WO# + status */}
                    <td className="px-2 py-1">
                      <div className="text-[14px] font-semibold">
                        <span className="text-blue-700 dark:text-blue-400 hover:underline" onClick={(e)=>{e.stopPropagation(); gotoWO(w.id);}} role="link" tabIndex={0} onKeyDown={(e)=>{if(e.key==='Enter'){e.stopPropagation(); gotoWO(w.id);}}} aria-label={`Open work order ${w.workOrderNumber}`}>
                          {w.workOrderNumber}
                        </span>
                      </div>
                      <div className="text-[12px] text-gray-600 dark:text-gray-400">{w.statusName || ''}</div>
                    </td>
                    {/* Client */}
                    <td className="px-2 py-1">
                      <div className="text-[14px] font-semibold text-gray-900 dark:text-gray-100">{w.clientName}</div>
                      <div className="text-[12px] text-gray-600 dark:text-gray-400">{[w.clientCity, w.clientState].filter(Boolean).join(', ')}</div>
                    </td>
                    {/* Equipment */}
                    <td className="px-2 py-1" onClick={(e)=>e.stopPropagation()}>
                      {w.equipmentId && w.equipmentName ? (
                        <Link
                          to={`/equipment/${w.equipmentId}`}
                          className="inline-flex items-center gap-1 text-[12px] text-blue-600 dark:text-blue-400 hover:underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Wrench className="h-3 w-3" />
                          <span>{w.equipmentName}</span>
                        </Link>
                      ) : (
                        <span className="text-[12px] text-gray-400 dark:text-gray-500">-</span>
                      )}
                    </td>
                    {/* Vendor */}
                    <td className="px-2 py-1">
                      <div className="flex items-center gap-2">
                        <div className="h-7 w-7 rounded-full flex items-center justify-center text-white text-[12px]" style={{backgroundColor: avatarColor(w.vendors?.[0]?.vendorName)}}>
                          {(w.vendors?.[0]?.vendorName || 'U').split(' ').map(s=>s[0]).slice(0,2).join('')}
                        </div>
                        <div className="text-[13px] text-gray-800 dark:text-gray-200">{w.vendors?.[0]?.vendorName || 'Unassigned'}</div>
                      </div>
                    </td>
                    {/* Priority */}
                    <td className="px-2 py-1">
                      <div className="flex items-center justify-center">
                        <div title={w.priorityName || ''} aria-label={`Priority ${w.priorityName || ''}`} className="h-7 w-7 rounded-full flex items-center justify-center text-white text-[12px] font-semibold" style={{backgroundColor: priorityColor(w.priorityId)}}>
                          {(w.priorityName || 'U').charAt(0).toUpperCase()}
                        </div>
                      </div>
                    </td>
                    {/* Due */}
                    <td className="px-2 py-1 text-[13px] text-gray-800 dark:text-gray-200">
                      {w.dueDate ? new Date(w.dueDate).toLocaleDateString() : ''}
                    </td>
                    {/* Days Remaining */}
                    <td className={`px-2 py-1 text-[13px] ${daysColorClass(d)}`}>
                      {d==null? '' : d<0 ? `${Math.abs(d)} days overdue` : `${d} days left`}
                    </td>
                    {/* Assigned To - with hover actions overlay */}
                    <td className="px-2 py-1 text-[13px] text-gray-800 dark:text-gray-200 relative">
                      <div className="group-hover:opacity-0 transition-opacity duration-200">
                        {w.assignedUserName || 'System'}
                      </div>
                      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-end gap-2 pr-2" onClick={(e)=>e.stopPropagation()}>
                        <Button variant="outline" size="sm" onClick={()=>gotoWO(w.id)} className="h-7 px-2 !bg-white dark:!bg-gray-800 !border-gray-300 dark:!border-gray-600 !text-gray-700 dark:!text-gray-300 hover:!bg-gray-50 dark:hover:!bg-gray-600" title="View Job Contact Sheet"> 
                          <span className="inline-flex items-center justify-center h-4 w-4 bg-gray-100 dark:bg-gray-700 rounded">
                            <Eye className="h-3.5 w-3.5"/> 
                          </span>
                        </Button>
                        <Link to={`/workorders/${w.id}/edit`} className="h-7 px-2 inline-flex items-center border rounded-md text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600" title="Edit Work Order">
                          <Edit className="h-4 w-4"/>
                        </Link>
                        <button
                          className="h-7 px-2 inline-flex items-center border rounded-md text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
                          onClick={async (e)=>{
                            e.stopPropagation();
                            try {
                              const response = await apiService.renderWorkOrder(w.id);
                              const blob = new Blob([response.data], { type: 'text/html' });
                              const url = window.URL.createObjectURL(blob);
                              const printWindow = window.open(url, '_blank');
                              if (printWindow) {
                                printWindow.onload = () => {
                                  printWindow.print();
                                  window.URL.revokeObjectURL(url);
                                };
                              }
                            } catch (error:any) {
                              console.error('Error printing work order:', error);
                              toast.error(`Error printing work order: ${error?.response?.data?.message || error.message}`);
                            }
                          }}
                          title="Print Work Order"
                        >
                          <Printer className="h-4 w-4"/>
                        </button>
                        <button
                          className="h-7 px-2 inline-flex items-center border rounded-md text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
                          onClick={(e)=>{ e.stopPropagation(); setSelectedWorkOrderForEmail(w); setIsEmailModalOpen(true); }}
                          title="Email Work Order"
                        >
                          <Mail className="h-4 w-4"/>
                        </button>
                        <button className="h-7 px-2 inline-flex items-center border rounded-md text-red-600 border-red-300 hover:bg-red-50" onClick={e=>{e.stopPropagation(); handleDelete(w.id);}} title="Delete Work Order">
                          <Trash2 className="h-4 w-4"/>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-700 overflow-hidden">
          <WorkOrdersCalendarPage
            filters={{
              search: debouncedSearch || undefined,
              assignedTo: assignedToFilter && assignedToFilter !== 'all' ? assignedToFilter : undefined,
              priorityId: priorityFilter && priorityFilter !== 'all' ? parseInt(priorityFilter) : undefined,
              statusId: undefined,
              tabGroup: activeTab.startsWith('group:') ? activeTab.substring('group:'.length) : undefined
            }}
          />
        </div>
      )}

      {/* Simple pagination (grid only) */}
      {viewMode==='grid' && (
        <div className="flex items-center justify-between text-sm">
          <div className="text-gray-600 dark:text-gray-400">Page {page} • {total} total</div>
          <div className="space-x-2">
            <Button variant="outline" size="sm" onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page===1}>Prev</Button>
            <Button variant="outline" size="sm" onClick={()=>setPage(p=>p+1)} disabled={items.length < pageSize}>Next</Button>
          </div>
        </div>
      )}

      {/* Bulk Edit Modal */}
      <BulkEditModal
        isOpen={isBulkEditModalOpen}
        onClose={() => setIsBulkEditModalOpen(false)}
        selectedWorkOrders={items?.filter(w => selectedIds.has(w.id)) || []}
        onSuccess={handleBulkEditSuccess}
      />

      <EmailImportModal
        isOpen={isEmailImportModalOpen}
        onClose={() => setIsEmailImportModalOpen(false)}
      />

      {/* Floating Action Button for New Work Order */}
      {viewMode==='grid' && (
        <button
          onClick={() => navigate('/workorders/create')}
          className="fixed bottom-6 right-6 z-50 bg-accent-color hover:bg-accent-color-600 text-white rounded-full p-4 shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-accent-color-100"
          title="Create New Work Order"
        >
          <Plus className="h-6 w-6" />
        </button>
      )}

      {/* Email Modal */}
      <Modal
        isOpen={isEmailModalOpen}
        onClose={() => { setIsEmailModalOpen(false); setSelectedWorkOrderForEmail(null); }}
        title="Email Work Order"
        maxWidth="md"
      >
        {selectedWorkOrderForEmail && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Work Order</label>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {selectedWorkOrderForEmail.workOrderNumber} - {selectedWorkOrderForEmail.clientName}
              </div>
            </div>

            <div>
              <label htmlFor="recipientEmail" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Recipient Email <span className="text-red-500">*</span></label>
              <input id="recipientEmail" type="email" className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100" placeholder="name@example.com" />
            </div>

            <div>
              <label htmlFor="customMessage" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Message</label>
              <textarea id="customMessage" className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100" rows={4} placeholder="Optional message..." />
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <Button variant="outline" onClick={() => { setIsEmailModalOpen(false); setSelectedWorkOrderForEmail(null); }}>
                Cancel
              </Button>
              <Button onClick={() => {
                const recipientEmail = (document.getElementById('recipientEmail') as HTMLInputElement)?.value;
                const customMessage = (document.getElementById('customMessage') as HTMLTextAreaElement)?.value;
                if (!recipientEmail) { toast.error('Please enter a recipient email address'); return; }
                if (selectedWorkOrderForEmail) {
                  apiService.sendWorkOrderEmail(selectedWorkOrderForEmail.id, recipientEmail, customMessage || undefined)
                    .then(() => { toast.success('Work order email sent successfully!'); setIsEmailModalOpen(false); setSelectedWorkOrderForEmail(null); })
                    .catch((error:any) => { console.error('Error sending email:', error); toast.error(`Error sending email: ${error?.response?.data?.message || error.message}`); });
                }
              }}>Send Email</Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <ConfirmDialog
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setWorkOrderToDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Delete Work Order"
        message={`Are you sure you want to delete work order "${workOrderToDelete?.workOrderNumberFormatted || workOrderToDelete?.workOrderNumber}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isLoading={deleteWorkOrderMutation.isPending}
      />
    </div>
  );
};

export default WorkOrdersPage;


